// File excluded from token migration due to dynamic nature
// https://atlassian.slack.com/archives/CFJ9DU39U/p1709665443238399
import type { StyledProps } from '@compiled/react';
import { styled, keyframes } from '@compiled/react';

import type { ButtonProps } from '@atlaskit/button/button';
import Button from '@atlaskit/button/button';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-atlaskit-theme
import { layers } from '@atlaskit/theme/constants';
import type {
	ComponentType,
	ClassAttributes,
	HTMLAttributes,
	RefAttributes,
	DO_NOT_USE_OR_YOU_WILL_BE_FIRED_CALLBACK_REF_RETURN_VALUES,
	RefObject,
} from 'react';

type BannerType = {
	isContentCreationPanelOpen: boolean;
	fade: boolean;
};

export const templateSidebarWidth = 320;

const shrinkUp = keyframes({
	'0%': {
		transform: 'translateY(0)',
	},
	'100%': {
		transform: 'translateY(-100%)',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TemplateBannerContainer: ComponentType<
	BannerType & ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div<BannerType>(
	{
		display: 'flex',
		height: '174px',
		backgroundSize: 'cover',
		backgroundRepeat: 'no-repeat',

		position: 'absolute',
		left: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		right: `${({ isContentCreationPanelOpen }) =>
			isContentCreationPanelOpen ? `${templateSidebarWidth}px` : 0}`,
		zIndex: layers.card(),
		'@media (max-width: 375px)': {
			height: '300px',
		},
		'@media (max-width: 1024px)': {
			flexDirection: 'column-reverse',
			height: '291px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		[`@media (max-width: ${1024 + templateSidebarWidth}px)`]: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			flexDirection: `${({ isContentCreationPanelOpen }) =>
				isContentCreationPanelOpen && 'column-reverse'}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			height: `${({ isContentCreationPanelOpen }) => isContentCreationPanelOpen && '291px'}`,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ fade }) =>
		fade && {
			animation: `${shrinkUp} 450ms ease-in-out`,
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledButton: ComponentType<
	Omit<ButtonProps & RefAttributes<HTMLElement>, 'ref'> & {
		ref?:
			| ((
					instance: HTMLElement | null,
			  ) =>
					| void
					| DO_NOT_USE_OR_YOU_WILL_BE_FIRED_CALLBACK_REF_RETURN_VALUES[keyof DO_NOT_USE_OR_YOU_WILL_BE_FIRED_CALLBACK_REF_RETURN_VALUES])
			| RefObject<HTMLElement>
			| null
			| undefined;
	} & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled(Button)({
	// requires the specificity and the button base includes !important :(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		background: token('color.background.discovery.bold'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text.inverse')} !important`,
		width: '110px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&:hover': {
		background: token('color.background.discovery.bold.hovered'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text.inverse')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&:active': {
		background: token('color.background.discovery.bold.pressed'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text.inverse')} !important`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LeftWrapper: ComponentType<
	ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	position: 'relative',
	height: '100%',
	display: 'flex',
	flex: 1,
	justifyContent: 'center',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RightWrapper: ComponentType<
	{
		isContentCreationPanelOpen: boolean;
	} & ClassAttributes<HTMLDivElement> &
		HTMLAttributes<HTMLDivElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div<{ isContentCreationPanelOpen: boolean }>({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	flex: 1,
	paddingTop: token('space.300'),
	'@media (max-width: 1024px)': {
		paddingLeft: token('space.300'),
		paddingTop: token('space.300'),
		flex: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`@media (max-width: ${1024 + templateSidebarWidth}px)`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		paddingLeft: `${({ isContentCreationPanelOpen }) => isContentCreationPanelOpen && '24px'}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		paddingTop: `${({ isContentCreationPanelOpen }) => isContentCreationPanelOpen && '24px'}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		flex: `${({ isContentCreationPanelOpen }) => isContentCreationPanelOpen && 'none'}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TitleWrapper: ComponentType<
	ClassAttributes<HTMLSpanElement> & HTMLAttributes<HTMLSpanElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.span({
	font: token('font.heading.medium'),
	color: token('color.text'),
	marginBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BodyWrapper: ComponentType<
	{
		isContentCreationPanelOpen: boolean;
	} & ClassAttributes<HTMLSpanElement> &
		HTMLAttributes<HTMLSpanElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.span<{ isContentCreationPanelOpen: boolean }>({
	font: token('font.body'),
	color: token('color.text'),
	marginBottom: token('space.200'),
	width: '100%',
	overflowWrap: 'break-word',
	wordWrap: 'break-word',
	maxWidth: '427px',
	'@media (max-width: 1024px)': {
		maxWidth: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`@media (max-width: ${1024 + templateSidebarWidth}px)`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		maxWidth: `${({ isContentCreationPanelOpen }) => isContentCreationPanelOpen && 'none'}`,
	},
});
